export const mapChargeStatusToDb = (statusValue) => {
  const status = String(statusValue || '').toLowerCase();
  const map = {
    pendiente: 'pending',
    pagado: 'paid',
    cancelado: 'cancelled',
    procesando: 'processing',
    rechazado: 'rejected',
    paid: 'paid',
    pending: 'pending',
    cancelled: 'cancelled',
    canceled: 'cancelled',
    processing: 'processing',
    rejected: 'rejected',
    failed: 'rejected',
  };
  return map[status] || status;
};

// VirtualPOS responde sus errores con HTTP 200 y el NOK en el body.
export const isVirtualPosErrorResponse = (response) => {
  if (!response) return false;
  const code = Number(response?.code);
  return (
    !!response?.error?.error_code ||
    String(response?.status || '').toUpperCase() === 'NOK' ||
    (Number.isFinite(code) && code >= 400)
  );
};

// Campos que administra el dunning y no vienen de VirtualPOS: sobreviven a
// cualquier refresco de metadata.
const pickCamposDunning = (metadata) => ({
  ...(metadata?.emails ? { emails: metadata.emails } : {}),
  ...(metadata?.retry ? { retry: metadata.retry } : {}),
  ...(metadata?.dunning_stage ? { dunning_stage: metadata.dunning_stage } : {}),
  ...(metadata?.change_card ? { change_card: metadata.change_card } : {}),
});

// Lo que el PSP declara del cobro dentro del envelope. DOS FORMAS posibles,
// una por cada recurso de VirtualPOS (ver virtualPosService.js
// getChargeById/getPaymentById): un cargo de suscripción trae
// `charge.payment.order`; un pago único (GET /v3/payment/{id}) NO tiene
// `charge` en absoluto — el `order` cuelga directo de `payment`. Antes de
// 2026-08-20 sólo se leía la primera forma: contra un pago único esto volvía
// `{}` con la respuesta YA BUENA del PSP, y `paid` se escribía sin monto ni
// liquidación — el mismo síntoma que el bug de `amount_paid` nulo documentado
// más abajo, pero disparado por la forma del envelope en vez de por status.
//
// Es la ÚNICA fuente de los pesos cursados — VirtualPOS mantiene el plan en UF
// y convierte él en cada `charge_date`, así que Yool nunca los calculó — y del
// depósito (fee, neto y fecha declarada).
//
// Mismo recorrido y misma semántica que `extraerMontoEnPesos` y
// `leerPrimerDeposito` de payment (`src/adapters/virtualpos.adapter.ts`;
// `getStatus()` lee `charge.payment.order`, `getPaymentLinkStatus()` lee
// `payment.order`). No se importan porque son otro repo: si allá cambia la
// forma del envelope, esto cambia con ella.
//
// El monto va TAL CUAL lo informó el PSP, sin redondear: es lo que cobró, no una
// cuenta nuestra. Tolerante a propósito — lo que el envelope no traiga no se
// inventa, queda `undefined` y no se escribe.
const leerOrderDelEnvelope = (envelope) =>
  envelope?.charge?.payment?.order ?? envelope?.payment?.order;

const leerCobroDelEnvelope = (envelope) => {
  const order = leerOrderDelEnvelope(envelope);
  if (!order) return {};

  const numero = (v) => {
    if (v === undefined || v === null || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const deposito = Array.isArray(order.deposits) ? order.deposits[0] : undefined;

  return {
    montoClp: numero(order.amount),
    autorizadoEn: order.authorized_at ? String(order.authorized_at) : undefined,
    feeClp: numero(deposito?.processing_fee),
    payoutClp: numero(deposito?.payout_amount),
    fechaDeposito: deposito?.payout_date
      ? String(deposito.payout_date).slice(0, 10)
      : undefined,
  };
};

/**
 * @param {object|null} newMetadata envelope recién traído de VirtualPOS
 * @param {object|null} existingMetadata `payment_metadata` guardada
 * @param {{processed_at?: string|null}|null} existingCharge la fila del cargo.
 *   Solo se usa para no pisar un `processed_at` ya registrado. Sin ella el monto
 *   se escribe igual, pero la fecha no: no hay con qué saber si sobra.
 */
export const buildChargeUpdatePayload = (newMetadata, existingMetadata, existingCharge = null) => {
  if (!newMetadata) return null;

  // Una respuesta de error no es un snapshot del cobro. Si la escribimos encima
  // perdemos el último estado bueno y, como el dunning descarta todo cobro cuyo
  // payment_metadata trae NOK, el cobro queda fuera de cobranzas para siempre:
  // nunca recibe su comprobante y se cuenta como error en cada corrida.
  // Caso real: E-057 "no existe el cargo asociado al id informado".
  // El error se guarda anidado en sync_error para poder diagnosticar, nunca al
  // nivel superior, que es donde el dunning lo lee como cobro fallido.
  if (isVirtualPosErrorResponse(newMetadata)) {
    // Si la metadata guardada ya era un error (cobro envenenado por una corrida
    // anterior), no la arrastramos: conservamos solo lo que administra el dunning
    // para que el nivel superior quede limpio y el cobro vuelva a cobranzas.
    const previa = isVirtualPosErrorResponse(existingMetadata)
      ? pickCamposDunning(existingMetadata)
      : existingMetadata || {};
    return {
      payment_metadata: { ...previa, sync_error: newMetadata },
    };
  }

  // `charge.status` para un cargo de suscripción; para un pago único no hay
  // `charge` — el status vive en `payment.order.status` (mismo dato, mismo
  // vocabulario en español, otra forma de envelope — ver leerOrderDelEnvelope).
  const derivedStatus = mapChargeStatusToDb(
    newMetadata?.charge?.status ?? leerOrderDelEnvelope(newMetadata)?.status,
  );
  const mergedMetadata = existingMetadata
    ? { ...newMetadata, ...pickCamposDunning(existingMetadata) }
    : newMetadata;

  // DERIVAR `paid` NO ALCANZA. Hasta 2026-08-18 esto escribía el status y el
  // snapshot crudo, y nada más: la cuota quedaba `paid` con `amount_paid` y
  // `processed_at` en NULL teniendo los dos datos dentro de la metadata que se
  // acababa de guardar. Yool Pay lee columnas, no metadata, así que el día
  // mostraba la cuota entre las `cobradas_sin_monto` y sumaba $0 — «se hicieron
  // los cobros y no se ven». Peor: `sync-charges` de payment, el único que sabe
  // parsear este envelope, sólo mira cargos `pending`, de modo que un cargo que
  // llega acá a `paid` sin monto queda fuera del alcance de todo lo que podría
  // completarlo. Verificado en dev: 4 cuotas del 17-18 de agosto, $71.905.
  //
  // Se promueve SÓLO cuando el cargo quedó pagado: en `pendiente` o `rechazado`
  // no hay pesos cursados ni depósito que declarar.
  const cobro = derivedStatus === 'paid' ? leerCobroDelEnvelope(newMetadata) : {};

  // La fecha DECLARADA por el proveedor manda sobre cualquier estimación: el
  // contrato de liquidación (T+1, T+3 hábiles…) es comercial y Yool no lo
  // conoce. Mismos nombres que usa payment al conciliar (sync-charges.service),
  // porque los lee el mismo consumidor: la cuadratura del settlement.
  const liquidacion = {
    ...(cobro.feeClp !== undefined ? { psp_fee_clp: cobro.feeClp } : {}),
    ...(cobro.payoutClp !== undefined ? { psp_payout_amount_clp: cobro.payoutClp } : {}),
    ...(cobro.fechaDeposito
      ? { expected_deposit_date: cobro.fechaDeposito, expected_deposit_date_fuente: 'psp' }
      : {}),
    // La hora de autorización del PSP viaja sin zona ('2026-08-17 00:03:06') y
    // no se convierte a `processed_at`: interpretarla acá sería elegir un huso
    // por el proveedor. Queda cruda, que es como la puede auditar quien la mire.
    ...(cobro.autorizadoEn ? { psp_authorized_at: cobro.autorizadoEn } : {}),
  };

  // `processed_at` es cuándo Yool registró el cobro —misma semántica que payment
  // y que flow—, así que sólo se escribe si la fila no lo tenía: pisarlo movería
  // el registro al día del refresco.
  const registrarProcesado =
    cobro.montoClp !== undefined && existingCharge && !existingCharge.processed_at;

  return {
    payment_metadata: { ...mergedMetadata, ...liquidacion },
    ...(derivedStatus ? { status: derivedStatus } : {}),
    ...(cobro.montoClp !== undefined ? { amount_paid: cobro.montoClp } : {}),
    ...(registrarProcesado ? { processed_at: new Date().toISOString() } : {}),
  };
};

export const refreshChargeMetadata = async ({
  getChargeById,
  // Recurso de pago único (GET /v3/payment/{id}) — ver virtualPosService.js.
  // Opcional por compatibilidad, pero sin él un pago único no tiene cómo
  // reconsultarse: cae al `if (!fetchFn)` de abajo y no llama a nada.
  getPaymentById,
  supabase,
  dbChargeId,
  externalChargeId,
  existingMetadata,
  // La fila del cargo. Sólo para no pisar un `processed_at` ya registrado; sin
  // ella el monto se promueve igual y la fecha se deja como está.
  existingCharge = null,
}) => {
  if (!supabase || !dbChargeId || !externalChargeId) {
    return { updated: false, metadata: null, derivedStatus: null, updatePayload: null };
  }

  // LA RUTA LA DECIDE EL FORMATO DEL ID, que es lo único que dice a qué
  // recurso de VirtualPOS pertenece el cobro:
  //   `cid_<hex>`   → cargo de suscripción → GET /v3/charge/{cid}
  //   uuid de orden → pago único           → GET /v3/payment/{uuid}
  //
  // Las dos rutas siguen separadas: esto no prueba ambas, elige una.
  //
  // NO SE DERIVA DE `subscriptions.orden_pago_id`, y ese fue el bug del primer
  // arreglo del 2026-08-20: eso describe cómo Yool modela el cobro, no dónde
  // vive en el PSP. Un pago único puede estar en el recurso de CARGO (si se
  // creó como suscripción de largo 1) o en el de ORDEN (si se creó por link de
  // pago). Medido contra el sandbox con cid_2d99d629…, que TIENE orden_pago_id:
  // /v3/charge devolvió pagado con fee 5276, /v3/payment falló E-020. Acá ese
  // criterio era peor que en payment: E-020 entra por
  // `isVirtualPosErrorResponse` y se guarda como `sync_error`, o sea que
  // preguntar por el recurso equivocado ENVENENA la metadata del cobro — el
  // mismo daño que este archivo existe para evitar.
  const esCargoDeSuscripcion = String(externalChargeId).startsWith('cid_');
  const fetchFn = esCargoDeSuscripcion ? getChargeById : getPaymentById;
  if (!fetchFn) {
    return { updated: false, metadata: null, derivedStatus: null, updatePayload: null };
  }

  const response = await fetchFn(String(externalChargeId));
  const newMetadata = response || null;
  if (!newMetadata) {
    return { updated: false, metadata: null, derivedStatus: null, updatePayload: null };
  }
  const updatePayload = buildChargeUpdatePayload(
    newMetadata,
    existingMetadata || null,
    existingCharge,
  );
  if (!updatePayload) {
    return { updated: false, metadata: null, derivedStatus: null, updatePayload: null };
  }
  const { error } = await supabase
    .from('subscription_charges')
    .update(updatePayload)
    .eq('id', dbChargeId);
  if (error) throw error;
  return {
    updated: true,
    metadata: updatePayload.payment_metadata,
    derivedStatus: updatePayload.status || null,
    updatePayload,
  };
};
